'use client';

import { EnergyType, PlanFlow } from '@energy-retailer/constants';
import { useState, type ComponentProps, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDebouncedCallback } from 'use-debounce';

import { Typeahead } from '@/app/components/typeahead/typeahead';
import { trackAnalyticsEvent } from '@/app/lib/analytics';
import { getPageLink } from '@/app/lib/contentful';
import { Button } from '@/components/button';
import { CheckboxSelectionMessaging } from '@/components/checkbox-selection-messaging/checkbox-selection-messaging';
import { Checkbox } from '@/components/input';
import { Loader } from '@/components/loader';
import { RichText } from '@/components/rich-text';
import { Text } from '@/components/text';
import { ANALYTICS_EVENTS } from '@/constants';
import { useAddressLookup } from '@/lib/hooks/use-address-lookup';
import { useJoinFormStore } from '@/stores/join-form-store/join-form-store';

import type { HeroSearch as Props } from '@/app/generated/graphql';
import type { DataType } from '@/components/typeahead/typeahead.types';
import type { Document } from '@contentful/rich-text-types';
import type { AddressHeroSchema } from '@energy-retailer/schema';

type AddressLookupProps = Pick<
  Props,
  'heading' | 'body' | 'heroLink' | 'heroCtaText'
> &
  Pick<ComponentProps<typeof Typeahead>, 'onOptionSelect'> & {
    isLoadingSubmitData: boolean;
  };

declare const window: Window & { dataLayer?: Record<string, unknown>[] };

const AddressLookup = ({
  body,
  heading,
  heroCtaText = 'Compare Plans',
  heroLink,
  isLoadingSubmitData = false,
  onOptionSelect,
}: AddressLookupProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const {
    property,
    electricityChecked,
    electricityAvailable,
    electricityPlanSlug,
    gasChecked,
    gasAvailable,
    gasPlanSlug,
    setCheckboxChecked,
    flow,
  } = useJoinFormStore();
  const [searchQuery, setSearchQuery] = useState<string>(
    property.addressSearch
  );
  const addressLookup = useAddressLookup<DataType>(searchQuery);
  const [loaded, setLoaded] = useState<boolean>(false);

  const debouncedSetSearchQuery = useDebouncedCallback(setSearchQuery, 300);

  const addressData = Array.isArray(addressLookup.data)
    ? addressLookup.data
    : [];
  const { setValue } = useFormContext<AddressHeroSchema>();

  useEffect(() => {
    setValue('address', {
      id: property.address.id,
      name: property.addressSearch,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only needs to run on first mount
  }, []);

  // Used to prevent hydration error on persisted state load
  useEffect(() => {
    setLoaded(true);
  }, [addressLookup]);

  useEffect(() => {
    if (property.addressSearch === '' && inputRef.current) {
      inputRef.current.value = '';
    }
  }, [inputRef, property.addressSearch]);

  return (
    <div className="mx-e flex flex-col items-center rounded-rad-a bg-white px-e py-f text-center shadow md:mx-0 md:ml-[calc(((-1/12)*100%)-0.75rem)] md:w-[calc((10/12)*100%)] md:max-w-[550px] md:rounded-rad-b md:px-d md:py-e">
      {heading ? (
        <Text
          as="h1"
          variant="h1"
          className="px-c text-center md:px-b"
        >
          {heading}
        </Text>
      ) : null}
      {body ? (
        <div className="mt-a leading-none">
          <RichText
            document={body.json as Document}
            bodyVariant="body2"
          />
        </div>
      ) : null}
      {heroLink ? (
        <Button
          variant="tertiary"
          {...getPageLink(heroLink)}
          className="mt-c"
        >
          {heroLink.title}
        </Button>
      ) : null}
      {!loaded ? (
        <Loader />
      ) : (
        <>
          <div className="w-full pb-d pt-b md:pb-c">
            <Text
              variant="body2"
              className="px-c pb-b text-center md:px-b"
            >
              Enter your address to see accurate rates for your place.
            </Text>
            <Typeahead<AddressHeroSchema>
              id="address-lookup-input"
              inputRef={inputRef}
              name="address"
              placeholder="Your address..."
              list={addressData}
              onOptionSelect={onOptionSelect}
              onChange={debouncedSetSearchQuery}
            />
          </div>

          <div className="flex w-full flex-row items-center justify-center gap-c pb-5 pt-2">
            <Checkbox
              name="electricity"
              checked={electricityChecked}
              label="Electricity"
              onChange={() =>
                setCheckboxChecked(EnergyType.Electricity, !electricityChecked)
              }
              disabled={!electricityAvailable || Boolean(electricityPlanSlug)}
            />
            <Checkbox
              name="gas"
              checked={gasChecked && gasAvailable}
              label="Gas"
              onChange={() => setCheckboxChecked(EnergyType.Gas, !gasChecked)}
              disabled={!gasAvailable || Boolean(gasPlanSlug)}
            />
          </div>

          <CheckboxSelectionMessaging className="mb-6 text-[14px] lg:mb-7 lg:mt-2" />

          <Button
            disabled={
              (!electricityChecked && !gasChecked) ||
              (!electricityChecked && !gasAvailable) // gasChecked is true when gasAvailable is false
            }
            loading={isLoadingSubmitData}
            form="address-lookup-form"
            type="submit"
            onClick={() => {
              if (window.dataLayer && !!property.address.full) {
                if (flow === PlanFlow.DualFuel) {
                  trackAnalyticsEvent(
                    ANALYTICS_EVENTS.checkboxesSelections
                      .dual_fuel_quote_flow_selected,
                    { postcode: property.address.postcode }
                  );
                } else if (flow === PlanFlow.Electricity) {
                  trackAnalyticsEvent(
                    ANALYTICS_EVENTS.checkboxesSelections
                      .electricity_quote_flow_selected,
                    { postcode: property.address.postcode }
                  );
                } else if (flow === PlanFlow.Gas) {
                  trackAnalyticsEvent(
                    ANALYTICS_EVENTS.checkboxesSelections
                      .gas_quote_flow_selected,
                    { postcode: property.address.postcode }
                  );
                }
              }
            }}
          >
            {heroCtaText ?? 'Compare Plans'}
          </Button>
        </>
      )}
    </div>
  );
};

export { AddressLookup };
